<template>
  <div
    class="drawer__list-item group"
    :class="{
      'is-editing': isBeingEdited || isBeingDeleted,
      'is-loading': isLoading
    }"
    :key="label.id"
    :style="'--label-color:' + label.color"
  >
    <transition name="switch" appear mode="out-in" @after-enter="focusInput()">
      <!-- Display Mode -->
      <div
        v-if="!isBeingEdited && !isBeingDeleted"
        key="display"
        class="flex items-center gap-3 w-full"
      >
        <!-- Drag Handle -->
        <span class="handle cursor-move text-neutral-400 hover:text-neutral-600 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
          </svg>
        </span>

        <!-- Color Dot -->
        <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: label.color }"></span>

        <!-- Name (clickable to edit) -->
        <span
          class="flex-1 text-sm font-medium text-neutral-700 cursor-pointer hover:text-neutral-900 transition-colors truncate"
          @click="startEditing()"
        >
          {{ label.name }}
        </span>

        <!-- Actions (show on hover) -->
        <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            class="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
            :title="$t('Edit')"
            @click="startEditing()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            v-if="!label.act_as_done"
            type="button"
            class="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            :title="$t('Delete')"
            @click="deleteConfirmation()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <div
        class="drawer__form w-full"
        v-else-if="isBeingEdited && !isBeingDeleted"
        key="form"
      >
        <div class="drawer__form-row">
          <input
            type="text"
            class="input flex-1"
            v-model="newName"
            @keydown.enter.prevent="handleSave()"
            @keydown.esc.prevent="handleClose()"
            :placeholder="$t('Status name')"
            ref="input"
          />

          <swatch-color-picker
            v-model="newColor"
            dropdown-position="right"
          />
        </div>

        <!-- Visibility toggles -->
        <div class="mt-3 divide-y divide-neutral-100">
          <label class="setting-row">
            <span class="pr-3">
              <span class="setting-row__title">{{ $t('Roadmap') }}</span>
              <span class="setting-row__desc">{{ $t('Show posts with this status on the roadmap') }}</span>
            </span>
            <input
              type="checkbox"
              class="sr-only"
              :checked="!hideRoadmap"
              @change="hideRoadmap = !$event.target.checked"
            />
            <span class="mini-toggle__track"><span class="mini-toggle__knob"></span></span>
          </label>

          <label class="setting-row">
            <span class="pr-3">
              <span class="setting-row__title">{{ $t('Board') }}</span>
              <span class="setting-row__desc">{{ $t('Show posts with this status on boards') }}</span>
            </span>
            <input
              type="checkbox"
              class="sr-only"
              :checked="!hideBoards"
              @change="hideBoards = !$event.target.checked"
            />
            <span class="mini-toggle__track"><span class="mini-toggle__knob"></span></span>
          </label>

          <label class="setting-row">
            <span class="pr-3">
              <span class="setting-row__title">{{ $t('Voting') }}</span>
              <span class="setting-row__desc">{{ $t('Allow new upvotes on posts with this status') }}</span>
            </span>
            <input
              type="checkbox"
              class="sr-only"
              :checked="!disableVotes"
              @change="disableVotes = !$event.target.checked"
            />
            <span class="mini-toggle__track"><span class="mini-toggle__knob"></span></span>
          </label>
        </div>

        <div v-if="error" class="text-sm text-red-600 mt-1">
          {{ error }}
        </div>

        <div class="drawer__form-actions">
          <button
            v-if="!label.act_as_done"
            type="button"
            class="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
            @click.prevent="deleteConfirmation()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
            {{ $t('Delete') }}
          </button>
          <span v-else></span>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="text-sm text-neutral-600 hover:text-neutral-800 font-medium"
              @click.prevent="handleClose()"
            >
              {{ $t('Cancel') }}
            </button>
            <button
              type="button"
              class="btn-main text-sm px-3 py-1.5"
              @click="handleSave()"
            >
              {{ $t('Save') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation -->
      <div
        class="w-full py-2 text-center"
        v-else-if="isBeingDeleted"
        key="delete"
      >
        <p class="text-sm text-neutral-600 mb-3">
          {{ $t('Delete this status? Posts with this status will become unassigned.') }}
        </p>
        <div class="flex items-center justify-center gap-3">
          <button
            type="button"
            class="btn-secondary text-sm px-3 py-1.5"
            @click.prevent="isBeingDeleted = false"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="btn-danger text-sm px-3 py-1.5"
            @click.prevent="destroyLabel()"
          >
            {{ $t('Delete') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SwatchColorPicker from "./swatch-color-picker.vue";

export default {
  props: ["original-label", "project-id", "status-name"],

  data: function() {
    return {
      label: this.originalLabel,
      newName: this.originalLabel.name,
      newColor: this.originalLabel.color,
      hideRoadmap: this.originalLabel.hide_roadmap,
      hideBoards: this.originalLabel.hide_boards,
      disableVotes: this.originalLabel.disable_votes,
      isBeingEdited: false,
      isBeingDeleted: false,
      isLoading: false,
      error: ""
    };
  },

  mounted: function() {
    this.keydownHandler = e => {
      if (e.key === "Escape") {
        this.handleClose();
      }
    };
    window.addEventListener("keydown", this.keydownHandler);
  },

  components: {
    SwatchColorPicker
  },

  methods: {
    startEditing() {
      this.isBeingEdited = true;
    },

    focusInput() {
      if (this.isBeingEdited && !this.isBeingDeleted) {
        this.$nextTick(() => {
          this.$refs.input && this.$refs.input.focus();
        });
      }
    },

    handleClose() {
      this.isBeingEdited = false;
      this.isBeingDeleted = false;
      this.newName = this.label.name;
      this.newColor = this.label.color;
      this.error = "";
    },

    handleSave() {
      this.updateLabel(true);
    },

    deleteConfirmation() {
      this.isBeingDeleted = true;
      this.isBeingEdited = true;
    },

    destroyLabel() {
      this.isLoading = true;

      Rails.ajax({
        url: "/statuses/" + this.label.id,
        type: "DELETE",
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.handleClose();
          this.$emit("destroyedLabel", data);
          this.isLoading = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isLoading = false;
        }
      });
    },

    updateLabel(closeAfterSave = false) {
      if (!this.newName || this.newName === this.statusName.not_set) {
        this.error = "Status name is required";
        return;
      }

      // Only show loading state for explicit saves, not auto-saves
      if (closeAfterSave) {
        this.isLoading = true;
      }
      this.error = "";

      let params = new FormData();
      if (this.originalLabel.name !== this.newName) {
        params.append("label[name]", this.newName);
        params.append("label[project_id]", this.projectId);
      }
      params.append("label[color]", this.newColor);
      params.append("label[hide_roadmap]", this.hideRoadmap);
      params.append("label[hide_boards]", this.hideBoards);
      params.append("label[disable_votes]", this.disableVotes);

      Rails.ajax({
        url: "/statuses/" + this.label.id,
        type: "PATCH",
        data: params,
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          if (data.error) {
            this.error = data.error;
          } else {
            this.label.name = this.newName;
            this.label.color = this.newColor;
            this.label.hide_roadmap = this.hideRoadmap;
            this.label.hide_boards = this.hideBoards;
            this.label.disable_votes = this.disableVotes;
            this.$emit("updatedLabel", data);
            if (closeAfterSave) {
              this.isBeingEdited = false;
              this.isLoading = false;
            }
          }
        },
        error: (data, status) => {
          console.log(data);
          if (closeAfterSave) {
            this.isLoading = false;
          }
        }
      });
    }
  },

  watch: {
    newColor: function() {
      // Auto-save color changes (don't close the form)
      if (this.isBeingEdited && this.newName) {
        this.updateLabel(false);
      }
    }
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.keydownHandler);
  }
};
</script>

<style scoped>
.switch-enter,
.switch-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.switch-enter-to,
.switch-leave {
  opacity: 1;
  transform: translateY(0);
}

.switch-enter-active,
.switch-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0;
  cursor: pointer;
  margin-bottom: 0;
}

.setting-row__title {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: theme('colors.neutral.800');
  line-height: 1.4;
}

.setting-row__desc {
  display: block;
  font-size: 0.75rem;
  color: theme('colors.neutral.700');
  line-height: 1.4;
  margin-top: 1px;
}

/* Compact version of the app-wide .button--toggle switch (see settings pages) */
.mini-toggle__track {
  position: relative;
  flex-shrink: 0;
  width: 28px;
  height: 16px;
  border-radius: 9999px;
  background-color: #f5f5f5;
  border: 1px solid gainsboro;
  transition: 0.3s ease all;
}

.mini-toggle__knob {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #d2d2d2;
  transition: 0.3s ease all, left 0.3s cubic-bezier(0.18, 0.89, 0.35, 1.15);
}

.setting-row input:checked + .mini-toggle__track {
  background-color: #d1f0e1;
  border-color: #a3e6c6;
}

.setting-row input:checked + .mini-toggle__track .mini-toggle__knob {
  left: 13px;
  background-color: #3bd087;
}
</style>
